import { AppStateService } from 'src/app/core/services/app-state.service';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog, MatSnackBar } from '@angular/material';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of, timer, zip } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { Assessment } from 'src/app/core/api/assessment.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { DryEyeCategory } from 'src/app/core/remote-json/treatments/dry-eye-category.model';
import {
  Treatment,
  TreatmentsService
} from 'src/app/core/remote-json/treatments/treatments.service';
import { AssessmentBody } from 'src/app/econsult/assessment-body/assessment-body.model';
import { EConsultService } from 'src/app/econsult/service/econsult.service';
import { SignsAttachmentsService } from 'src/app/encounters/signs/signs-attachments-modal/signs-attachments.service';
import { TreatmentTemplateService } from 'src/app/logged-in-navbar/clinic-setup-modal/treatment-template/treatment-template.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import {
  hasImpressionsSectionContent,
  upsertImpressionsSection
} from 'src/app/shared/impressions/impressions-sections.util';
import { InterpretationComponent } from 'src/app/shared/interpretation/interpretation.component';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { EConsultFormGroup } from '../../../../econsult/econsult-form.model';
import { FormErrorScrollingService } from '../../../form-error-scrolling/form-error-scrolling.service';
import { RiskFactorsModalComponent } from '../../../risk-factors-report/risk-factors-modal.component';
import { PatientProfile } from '../../recommendations/models/patient.model';
import { TreatmentRecommendation } from '../../recommendations/models/treatment.model';
import { RecommendationService } from '../../recommendations/recommendations.service';
import { AttachTemplateModalComponent } from '../attach-template-modal/attach-template-modal.component';
import {
  RecommendationTemplate,
  TemplateRecommendation,
  getRecommendationTemplateByKey,
  recommendTemplatesFromImpressions
} from '../attach-template-modal/recommendation-template.model';
import { DryEyeMedicalHistoryFormGroup } from '../dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { PreOpService } from '../pre-op/pre-op.service';
import { TextFavoriteType } from './../../../../../API';
import { TextFavoriteService } from './../../../../logged-in-navbar/clinic-setup-modal/text-favorite/text-favorite.service';
import { DryEyeFormGroup } from './../dry-eye-form/dry-eye-form.model';
import { DryEyeExpansionPanel, DryEyeFormsService } from './../dry-eye-forms.service';
import { DryEyePredictionService } from './../dry-eye-prediction.service';
import { DryEyeAssessmentFormGroup } from './dry-eye-assessment-form.model';
import { TreatmentFiltersCDSService } from './treatment-filters/treatment-filters-cds.service';

export type TemplateChipState = 'recommended' | 'partial' | 'attached';
type RecommendationSeveritySectionKey = 'significant' | 'probable' | 'possible';
type TemplateRecommendationOrigin = 'default' | 'custom';

interface TemplateRecommendationSection {
  key: RecommendationSeveritySectionKey;
  label: string;
  recommendations: TemplateRecommendation[];
}

@Component({
  selector: 'csi-dry-eye-assessment-form',
  templateUrl: './dry-eye-assessment-form.component.html',
  styleUrls: ['./dry-eye-assessment-form.component.scss'],
  animations: [
    trigger('predictionAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class DryEyeAssessmentFormComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeAssessmentFormGroup;
  @Input() medicalHistoryFormGroup: DryEyeMedicalHistoryFormGroup;
  @Input() showImageRequiredError = false;
  @Input() showCategorySelection = false;
  @Input() showImpressionsWarning$: Observable<boolean>;
  @Input() expandAttachments: boolean;
  @Input() symptomsAndSignsFormGroup: DryEyeFormGroup;
  @Input() saveFn: () => Observable<Assessment>;
  @Input() isLocked: boolean;
  @Input() showAttachments: boolean = true;
  @Input() showImpressionsRecommendations: boolean = true;
  @Input() useAccordion: boolean = true;

  public isGeneratingTemplateRecommendations = false;
  public hasGeneratedTemplateRecommendations = false;

  public recommendedTemplateKey: string = '';
  public recommendedTemplate: RecommendationTemplate = null;

  public requiredErrors: string[];
  public normalizedErrors: string[];
  public readonly SymptomModes = SymptomModes;
  public readonly ExpansionPanel = DryEyeExpansionPanel;

  public readonly ML_VERSION = 3;

  public riskFactorTitle: string;

  public dryEyePrediction: string;

  public TextFavoriteType = TextFavoriteType;
  public waitingForPredict = false;

  public showAIRecommendations = false;
  public isGeneratingRecommendations = false;
  public aiPatientProfile: PatientProfile;

  public aiRecommendations?: TreatmentRecommendation;

  public templateRecommendations: TemplateRecommendation[] = [];
  public templateRecommendationSections: TemplateRecommendationSection[] = [];

  public attachedTemplateKeys = new Set<string>();

  public selectedTemplateRecommendation: TemplateRecommendation | null = null;
  public selectedTemplateRecommendationReason = '';

  public templateChipStateMap: { [key: string]: TemplateChipState } = {};
  public templateRecommendationOriginMap: { [key: string]: TemplateRecommendationOrigin } = {};
  public templateRecommendationNameMap: { [key: string]: string } = {};
  public readonly templateRecommendationOriginLabelMap: {
    [key in TemplateRecommendationOrigin]: string;
  } = {
    default: 'Default template',
    custom: 'Custom template'
  };
  public hasSelectedTreatments = false;

  private readonly recommendationSectionOrder: RecommendationSeveritySectionKey[] = [
    'significant',
    'probable',
    'possible'
  ];

  private readonly recommendationSectionLabelMap: {
    [key in RecommendationSeveritySectionKey]: string;
  } = {
    significant: 'Significant',
    probable: 'Probable',
    possible: 'Possible'
  };

  private customTemplatesByCopiedId: { [defaultTemplateId: string]: RecommendationTemplate } = {};
  private customTemplatesByKey: { [key: string]: RecommendationTemplate } = {};
  private customTemplatesByRecommendationKey: { [key: string]: RecommendationTemplate[] } = {};

  public get assessmentData() {
    return this.flattenedCDSValues();
  }

  isLoading = false;
  recommendations?: TreatmentRecommendation;
  error?: string;

  public categoryAndSeverityMatSpinnerOptions: MatProgressButtonOptions = {
    text: 'Predict Dry Eye Type And Severity',
    active: false,
    raised: true,
    buttonColor: 'primary',
    spinnerSize: 19,
    mode: 'indeterminate'
  };

  public severityMatSpinnerOptions: MatProgressButtonOptions = {
    text: 'Predict Dry Eye Severity Risk',
    active: false,
    raised: true,
    buttonColor: 'primary',
    spinnerSize: 19,
    mode: 'indeterminate'
  };

  typing = false;
  typingDone = false;
  private typingDoneTimer?: any;
  private typeTimer?: any;
  private lastProgrammaticTarget: string | null = null;

  @Input() doctorNotesPanelOpen = false;
  @Input() hostDoctorNotesPanel = false;
  @Output() doctorNotesPanelClosed = new EventEmitter<void>();

  public closeDoctorNotesPanel() {
    this.doctorNotesPanelClosed.emit();
  }

  @HostListener('document:keydown.escape')
  public onEscapeCloseDoctorNotes() {
    if (this.hostDoctorNotesPanel && this.doctorNotesPanelOpen) {
      this.doctorNotesPanelClosed.emit();
    }
  }

  get discussionSummary(): string | null {
    const scribeData =
      this.formGroup && this.formGroup.controls.aiScribeResult
        ? this.formGroup.controls.aiScribeResult.value
        : null;
    const conv = scribeData && scribeData.conversationResult ? scribeData.conversationResult : null;
    return conv ? conv.discussionSummary : null;
  }

  constructor(
    public filterService: TreatmentFiltersCDSService,
    private econsultFormGroup: EConsultFormGroup,
    private formErrorScrollingService: FormErrorScrollingService,
    private matDialog: MatDialog,
    private dryEyePredictionService: DryEyePredictionService,
    private changeDetector: ChangeDetectorRef,
    public staffService: StaffService,
    private textFavoriteService: TextFavoriteService,
    private confirmModalService: ConfirmModalService,
    public dryEyeFormsService: DryEyeFormsService,
    public preOpService: PreOpService,
    private recommendationService: RecommendationService,
    public signsAttachmentsService: SignsAttachmentsService,
    private snackBar: MatSnackBar,
    private treatmentsService: TreatmentsService,
    @Optional() private econsultService: EConsultService,
    private treatmentTemplateService: TreatmentTemplateService,
    private appStateService: AppStateService
  ) {}

  public HIDDEN_MODAL_CONFIG = {
    width: '0px',
    height: '0px',
    panelClass: 'hidden-modal',
    hasBackdrop: false
  };

  ngOnInit() {
    this.textFavoriteService.treatmentControlMap = {
      otcEyeDrops: this.formGroup.controls.recommendedEyeDrops,
      rxEyeDrops: this.formGroup.controls.recommendedRxEyeDrops,
      exercises: this.formGroup.controls.recommendedExercises,
      procedures: this.formGroup.controls.recommendedProcedures
    };
    this.dryEyePredictionService.symptomsAndSignsFormGroup = this.symptomsAndSignsFormGroup;
    this.dryEyePredictionService.medicalHistoryFormGroup = this.medicalHistoryFormGroup;
    this.dryEyePredictionService.patient = this.appStateService.getCurrentPatient();

    this.watchRecommendationControlsForChipState();
    this.updateHasSelectedTreatments();

    this.indexCustomTemplatesForOverrides();
    this.treatmentTemplateService.treatmentTemplatesFormControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.indexCustomTemplatesForOverrides();
        this.recomputeTemplateRecommendationOrigins();
        this.recomputeTemplateChipStates();
        this.changeDetector.markForCheck();
      });
  }

  get severityClass(): 'mild' | 'moderate' | 'severe' | 'error' | '' {
    if (this.requiredErrors && this.requiredErrors.length) return 'error';
    if (!this.dryEyePrediction) return '';
    const severity = this.dryEyePrediction.toLowerCase();
    if (severity.startsWith('mild')) return 'mild';
    if (severity.startsWith('moderate')) return 'moderate';
    if (severity.startsWith('severe')) return 'severe';
    return '';
  }

  get hasImages(): boolean {
    return this.formGroup.controls.images.value && this.formGroup.controls.images.value.length > 0;
  }

  get doctorNotesLength(): number {
    const ctrl = this.formGroup && this.formGroup.controls && this.formGroup.controls.doctorNotes;
    if (!ctrl) {
      return 0;
    }
    const currentNotes = ctrl.value;
    if (currentNotes === null || currentNotes === undefined) {
      return 0;
    }
    return currentNotes.toString().length;
  }

  get hasAssessmentSnapshot(): boolean {
    return !!this.dryEyePrediction || !!this.getSelectedCategoryLabel();
  }

  get hasSignsSummary(): boolean {
    return this.getMeaningfulSignEntries().length > 0;
  }

  public addDoctorNotesTimestamp() {
    const timestamp = new Date().toLocaleString();
    this.appendDoctorNotesLine('[' + timestamp + ']');
  }

  public addDoctorNotesAssessmentSnapshot() {
    const parts: string[] = [];
    const categoryLabel = this.getSelectedCategoryLabel();

    if (this.dryEyePrediction) {
      parts.push('AI prediction: ' + this.dryEyePrediction);
    }
    if (categoryLabel) {
      parts.push('Selected category: ' + categoryLabel);
    }
    if (!parts.length) {
      parts.push('No prediction/category selected yet.');
    }

    this.appendDoctorNotesLine(parts.join(' | '));
  }

  public addDoctorNotesSignsSummary() {
    const signEntries = this.getMeaningfulSignEntries();

    if (!signEntries.length) {
      this.appendDoctorNotesLine('No signs captured yet.');
      return;
    }

    const summary = signEntries
      .map(entry => this.toHumanLabel(entry.key) + ': ' + this.formatSignsValue(entry.value))
      .join(' | ');

    this.appendDoctorNotesLine(summary);
  }

  private getMeaningfulSignEntries(): { key: string; value: any }[] {
    const signsValues =
      this.symptomsAndSignsFormGroup && this.symptomsAndSignsFormGroup.value
        ? this.symptomsAndSignsFormGroup.value
        : null;

    if (!signsValues) return [];

    return Object.keys(signsValues)
      .filter(
        key => key !== 'assessmentMethod' && key !== 'schemaVersion' && !key.endsWith('_METHOD')
      )
      .map(key => ({ key, value: signsValues[key] }))
      .filter(entry => this.hasMeaningfulSignsValue(entry.value));
  }

  public clearDoctorNotes() {
    if (this.isLocked) return;
    if (!this.doctorNotesLength) return;

    this.confirmModalService
      .show(
        'doctorNotes.confirm.clearTitle',
        'doctorNotes.confirm.clearBody',
        'doctorNotes.confirm.clearButton',
        'Cancel'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;

        const doctorNotesControl = this.formGroup.controls.doctorNotes;
        doctorNotesControl.setValue('');
        doctorNotesControl.markAsDirty();
        doctorNotesControl.markAsTouched();
      });
  }

  public showCDSModal() {
    this.matDialog.open(InterpretationComponent, {
      data: {
        patientData: this.flattenedCDSValues(),
        impressionsControl: this.formGroup.controls.impressions
      },
      minWidth: '60vw'
    });
  }

  public openRiskFactorsReportModal() {
    const isPreOpScreening =
      this.symptomsAndSignsFormGroup.controls.assessmentMethod.value ===
      SymptomModes.PreOpScreening;

    this.matDialog.open(RiskFactorsModalComponent, {
      data: {
        medicalHistoryData: this.medicalHistoryFormGroup.value,
        impressionsControl: this.formGroup.controls.impressions,
        isPreOpScreening
      }
    });
  }

  public scrollToFirstError() {
    this.econsultFormGroup.submitted = true;
    this.formErrorScrollingService.scrollToFirstInvalidControl();
  }

  private flattenedCDSValues() {
    const data: AssessmentBody = this.econsultFormGroup.getRawValue() as AssessmentBody;

    const medicalHistoryFormValues = data.medicalHistoryForm;
    const dryEyeFormValues = data.dryEyeForm;

    if (!!dryEyeFormValues && !!medicalHistoryFormValues) {
      Object.keys(medicalHistoryFormValues).forEach(key => {
        if (!!dryEyeFormValues[key] && key !== 'schemaVersion') {
          console.error(
            'Warning: Found a duplicate key used in medicalHistoryForm and dryEyeForm called `' +
              key +
              '`. This may cause CDSComponent to display incorrect values.'
          );
        }
      });
    }

    return {
      ...medicalHistoryFormValues,
      ...dryEyeFormValues
    };
  }

  predictDryEyeCategoryAndSeverity() {
    this.requiredErrors = [];
    this.waitingForPredict = true;
    this.categoryAndSeverityMatSpinnerOptions.active = true;
    this.dryEyePredictionService
      .predictDryEyeCategoryAndSeverity()
      .pipe(
        switchMap(result => timer(2500).pipe(map(() => result))),
        finalize(() => {
          this.categoryAndSeverityMatSpinnerOptions.active = false;
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (result: string) => {
          this.dryEyePrediction = result;
          this.waitingForPredict = false;
          this.requiredErrors = [];
          this.formGroup.controls.mlPrediction.setValue({
            ...(this.formGroup.controls.mlPrediction.value || {}),
            [this.ML_VERSION]: result
          });
          this.changeDetector.markForCheck();
        },
        error: (errors: string[]) => {
          this.requiredErrors = errors;
          this.waitingForPredict = false;
          this.changeDetector.markForCheck();
        }
      });
  }

  predictDryEyeSeverity() {
    this.severityMatSpinnerOptions.active = true;

    this.dryEyePredictionService
      .predictDryEyeSeverity()
      .pipe(
        finalize(() => (this.severityMatSpinnerOptions.active = false)),
        untilDestroyed(this)
      )
      .subscribe((result: string) => {
        this.dryEyePrediction = result;
        this.changeDetector.markForCheck();
      });
  }

  public getInterpretation(): Observable<string> {
    return this.matDialog
      .open(InterpretationComponent, {
        ...this.HIDDEN_MODAL_CONFIG,
        data: {
          patientData: this.flattenedCDSValues(),
          impressionsControl: this.formGroup.controls.impressions,
          closeAfterProcess: true
        }
      })
      .afterClosed();
  }

  public getRiskFactors(): Observable<string> {
    return this.matDialog
      .open(RiskFactorsModalComponent, {
        ...this.HIDDEN_MODAL_CONFIG,
        data: {
          medicalHistoryData: this.medicalHistoryFormGroup.value,
          impressionsControl: this.formGroup.controls.impressions,
          closeAfterProcess: true
        }
      })
      .afterClosed();
  }

  public analyzeAndCopyToImpression() {
    this.confirmImpressionsOverwrite().subscribe(confirmed => {
      if (!confirmed) return;
      this.runAnalyzeAndCopyToImpression();
    });
  }

  private runAnalyzeAndCopyToImpression() {
    this.predictDryEyeCategoryAndSeverity();
    this.waitingForPredict = true;

    const interpretation$ = this.getInterpretation();
    const riskFactors$ = this.getRiskFactors();
    zip(interpretation$, riskFactors$).subscribe(
      (contents: string[]) => {
        const [interpretationContent, riskFactorsContent] = contents;
        const currentImpressions = (this.formGroup.controls.impressions.value || '').toString();
        const withUpdatedInterpretation = upsertImpressionsSection(
          currentImpressions,
          'interpretation',
          (interpretationContent || '').toString()
        );
        const withUpdatedRiskFactors = upsertImpressionsSection(
          withUpdatedInterpretation,
          'riskFactors',
          (riskFactorsContent || '').toString()
        );
        this.beginImpressionsTyping(withUpdatedRiskFactors);
        setTimeout(() => (this.waitingForPredict = false), 2500);
      },
      error => {
        console.error('Error in combining data for impression:', error);
        setTimeout(() => (this.waitingForPredict = false), 2500);
      }
    );
  }

  private confirmImpressionsOverwrite(): Observable<boolean> {
    if (!this.hasAiSectionsToOverwrite()) {
      return of(true);
    }

    return this.confirmModalService.show(
      'impression.confirm.overwriteAiSectionsTitle',
      'impression.confirm.overwriteAiSectionsBody',
      'impression.confirm.overwriteSectionsButton',
      'Cancel'
    );
  }

  private hasAiSectionsToOverwrite(): boolean {
    const currentImpressions = (this.formGroup.controls.impressions.value || '').toString();
    return (
      hasImpressionsSectionContent(currentImpressions, 'interpretation') ||
      hasImpressionsSectionContent(currentImpressions, 'riskFactors')
    );
  }

  private appendDoctorNotesLine(line: string) {
    if (this.isLocked) return;

    const doctorNotesControl = this.formGroup.controls.doctorNotes;
    const contentToAppend = (line || '').trim();
    if (!contentToAppend) return;

    const existingNotes = (doctorNotesControl.value || '').toString();
    const separator = existingNotes.length > 0 && !existingNotes.endsWith('\n') ? '\n' : '';
    doctorNotesControl.setValue(existingNotes + separator + contentToAppend);
    doctorNotesControl.markAsDirty();
    doctorNotesControl.markAsTouched();
  }

  private getSelectedCategoryLabel(): string {
    const selectedCategory = this.formGroup.controls.category.value;
    if (!selectedCategory) return '';

    if (selectedCategory.severity && selectedCategory.type) {
      return (
        this.toHumanLabel(selectedCategory.severity.toString()) +
        ' ' +
        this.toHumanLabel(selectedCategory.type.toString())
      );
    }

    return this.toDisplayLabel(selectedCategory);
  }

  private hasMeaningfulSignsValue(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.some(item => this.hasMeaningfulSignsValue(item));
    if (typeof value === 'object') {
      return Object.keys(value).some(key => this.hasMeaningfulSignsValue(value[key]));
    }

    return false;
  }

  private formatSignsValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) {
      const labels = value.map(item => this.toDisplayLabel(item)).filter(label => !!label);
      if (!labels.length) return '';
      const maxItems = 3;
      const clipped = labels.slice(0, maxItems).join(', ');
      return labels.length > maxItems
        ? clipped + ' +' + (labels.length - maxItems) + ' more'
        : clipped;
    }
    if (typeof value === 'object') {
      const keys = Object.keys(value).filter(
        key =>
          key !== 'method' && !key.endsWith('_METHOD') && this.hasMeaningfulSignsValue(value[key])
      );
      if (!keys.length) return '';
      const maxItems = 2;
      const clipped = keys
        .slice(0, maxItems)
        .map(key => this.toHumanLabel(key) + ': ' + this.formatSignsValue(value[key]))
        .join(', ');
      return keys.length > maxItems ? clipped + ' +' + (keys.length - maxItems) + ' more' : clipped;
    }

    return value.toString();
  }

  private toDisplayLabel(value: any): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value.toString().trim();
    }
    if (value.name) return value.name.toString().trim();
    if (value.label) return value.label.toString().trim();
    if (value.title) return value.title.toString().trim();
    return '';
  }

  private toHumanLabel(raw: string): string {
    return raw
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  beginImpressionsTyping(text: string) {
    const ctrl = this.formGroup.controls.impressions;
    this.typeOutToControl(ctrl, text);
  }

  skipTyping() {
    if (!this.typing) return;
    const ctrl = this.formGroup.controls.impressions;
    const target =
      this.lastProgrammaticTarget !== null && this.lastProgrammaticTarget !== undefined
        ? this.lastProgrammaticTarget
        : '';
    this.finishTyping(ctrl, target);
  }

  private typeOutToControl(ctrl: AbstractControl, target: string) {
    if (this.typeTimer) clearTimeout(this.typeTimer);
    this.typingDone = false;
    this.typing = true;
    this.changeDetector.markForCheck();
    this.lastProgrammaticTarget = target;

    const start = (ctrl.value || '').toString();
    const common = this.sharedPrefixLen(start, target);
    let i = Math.max(common, 0);

    ctrl.disable({ emitEvent: false });
    ctrl.setValue(target.slice(0, common), { emitEvent: false });

    const totalDurationMs = 2000;
    const startTime = performance.now();
    const totalDelta = target.length - common;

    const step = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / totalDurationMs, 1);

      i = Math.min(common + Math.round(totalDelta * progress), target.length);
      ctrl.setValue(target.slice(0, i), { emitEvent: false });

      if (progress >= 1) {
        return this.finishTyping(ctrl, target);
      }

      this.typeTimer = setTimeout(step, 16);
    };

    this.typeTimer = setTimeout(step, 16);
  }

  private finishTyping(ctrl: any, target: string) {
    if (this.typeTimer) clearTimeout(this.typeTimer);
    ctrl.setValue(target, { emitEvent: false });
    ctrl.enable({ emitEvent: false });
    this.typingDone = true;
    this.changeDetector.markForCheck();
    this.typing = false;
  }

  private sharedPrefixLen(a: string, b: string) {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    return i;
  }

  private normalizeCategory(raw: any): 'AQUEOUS_DEFICIENT' | 'EVAPORATIVE' | 'MIXED' {
    if (!raw) {
      console.warn('[normalizeCategory] No raw value provided, defaulting to MIXED');
      return 'MIXED';
    }

    const value = typeof raw === 'string' ? raw : raw.type || raw.category || '';

    const normalized = value.trim().toUpperCase();

    switch (normalized) {
      case 'AQUEOUS':
      case 'AQUEOUS_DEFICIENT':
        return 'AQUEOUS_DEFICIENT';
      case 'EVAPORATIVE':
        return 'EVAPORATIVE';
      case 'MIXED':
        return 'MIXED';
      default:
        console.warn('[normalizeCategory] Unknown category, defaulting to MIXED');
        return 'MIXED';
    }
  }

  private normalizeSeverity(raw: any): 'MILD' | 'MODERATE' | 'SEVERE' {
    if (!raw) {
      console.warn('[normalizeSeverity] No raw value, defaulting to MODERATE');
      return 'MODERATE';
    }

    const value = typeof raw === 'string' ? raw.split(' ')[0] : raw.severity || '';

    const normalized = value.trim().toUpperCase();

    switch (normalized) {
      case 'MILD':
      case 'MODERATE':
      case 'SEVERE':
        return normalized as any;
      default:
        console.warn('[normalizeSeverity] Unknown severity, defaulting to MODERATE');
        return 'MODERATE';
    }
  }

  private indexCustomTemplatesForOverrides(): void {
    const templates: any[] =
      this.treatmentTemplateService.treatmentTemplatesFormControl.value || [];

    const byCopied: { [defaultTemplateId: string]: RecommendationTemplate } = {};
    const byKey: { [key: string]: RecommendationTemplate } = {};
    const byRecKey: { [key: string]: RecommendationTemplate[] } = {};

    (templates || []).forEach((t: any) => {
      if (!t) return;

      const normalized = this.toRecommendationTemplateShape(t);
      const isSystemDefault = this.isSystemDefaultTemplate(t);
      const isHidden = this.isTemplateHidden(normalized);

      const idKey = normalized && normalized.id ? String(normalized.id).toLowerCase() : '';
      const friendlyKey =
        normalized && normalized.friendlyId ? String(normalized.friendlyId).toLowerCase() : '';

      if (!isHidden) {
        if (idKey) byKey[idKey] = normalized;
        if (friendlyKey) byKey[friendlyKey] = normalized;
      }

      const copiedId = t && t.copiedTreatmentTemplateId ? String(t.copiedTreatmentTemplateId) : '';
      if (!isHidden && copiedId) {
        byCopied[copiedId] = normalized;
      }

      if (isSystemDefault || isHidden) return;

      const recKeys: any[] = (t && (t.recommendationConditions as any)) || [];
      (recKeys || []).forEach((k: any) => {
        const key = k ? String(k).toLowerCase() : '';
        if (!key) return;

        if (!byRecKey[key]) byRecKey[key] = [];

        const exists = byRecKey[key].some(
          x => x && x.id && normalized.id && x.id === normalized.id
        );
        if (!exists) byRecKey[key].push(normalized);
      });
    });

    this.customTemplatesByCopiedId = byCopied;
    this.customTemplatesByKey = byKey;
    this.customTemplatesByRecommendationKey = byRecKey;
  }

  private getCustomTemplatesForRecommendationKey(key: string): RecommendationTemplate[] {
    const k = key ? String(key).toLowerCase() : '';
    return k &&
      this.customTemplatesByRecommendationKey &&
      this.customTemplatesByRecommendationKey[k]
      ? this.customTemplatesByRecommendationKey[k]
      : [];
  }

  private isSystemDefaultTemplate(template: any): boolean {
    const id = template && template.id ? String(template.id) : '';
    return !!(
      id &&
      this.treatmentTemplateService &&
      this.treatmentTemplateService.defaultTreatmentTemplateMap &&
      this.treatmentTemplateService.defaultTreatmentTemplateMap[id]
    );
  }

  private getTemplateRecommendationKey(template: RecommendationTemplate): string {
    if (!template) return '';
    if (template.friendlyId && String(template.friendlyId).trim()) {
      return String(template.friendlyId);
    }
    return template.id ? String(template.id) : '';
  }

  private getPreferredCustomTemplateForRecommendationKey(
    key: string
  ): RecommendationTemplate | null {
    const customTemplates = this.getCustomTemplatesForRecommendationKey(key);
    if (!customTemplates || customTemplates.length === 0) return null;

    const defaultTemplate = getRecommendationTemplateByKey(key);
    const defaultTemplateId =
      defaultTemplate && defaultTemplate.id ? String(defaultTemplate.id) : '';

    if (defaultTemplateId) {
      const copiedOverride = customTemplates.find(
        t =>
          t &&
          t.copiedTreatmentTemplateId &&
          String(t.copiedTreatmentTemplateId) === defaultTemplateId
      );
      if (copiedOverride) return copiedOverride;
    }

    return customTemplates[0];
  }

  private toRecommendationTemplateShape(t: any): RecommendationTemplate {
    return {
      ...t,
      id: t && t.id ? String(t.id) : '',
      friendlyId: t && t.friendlyId ? String(t.friendlyId) : '',
      exercises: this.toTreatmentIdArray(t && t.exercises),
      otcEyeDrops: this.toTreatmentIdArray(t && t.otcEyeDrops),
      rxEyeDrops: this.toTreatmentIdArray(t && t.rxEyeDrops),
      procedures: this.toTreatmentIdArray(t && t.procedures)
    };
  }

  private toTreatmentIdArray(items: any): string[] {
    return (items || [])
      .map((x: any) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (x.id) return String(x.id);
        return '';
      })
      .filter((id: string) => !!id);
  }

  private getHiddenTemplateIdSet(): Set<string> {
    const hiddenFromControl =
      (this.treatmentTemplateService &&
        this.treatmentTemplateService.clinicSetupService &&
        this.treatmentTemplateService.clinicSetupService.clinicSetupFormGroup &&
        this.treatmentTemplateService.clinicSetupService.clinicSetupFormGroup.controls &&
        this.treatmentTemplateService.clinicSetupService.clinicSetupFormGroup.controls
          .hiddenTreatmentTemplates &&
        this.treatmentTemplateService.clinicSetupService.clinicSetupFormGroup.controls
          .hiddenTreatmentTemplates.value) ||
      [];

    const hiddenFromService =
      (this.treatmentTemplateService &&
        this.treatmentTemplateService.clinicSetupService &&
        this.treatmentTemplateService.clinicSetupService.hiddenTreatmentTemplates) ||
      [];

    const hiddenIds = [...hiddenFromControl, ...hiddenFromService];
    return new Set(
      hiddenIds.map((id: any) => (id ? String(id).toLowerCase() : '')).filter((id: string) => !!id)
    );
  }

  private isTemplateHidden(template: RecommendationTemplate): boolean {
    if (!template) return false;

    const hiddenIds = this.getHiddenTemplateIdSet();
    if (!hiddenIds.size) return false;

    const id = template.id ? String(template.id).toLowerCase() : '';
    const friendlyId = template.friendlyId ? String(template.friendlyId).toLowerCase() : '';

    return (!!id && hiddenIds.has(id)) || (!!friendlyId && hiddenIds.has(friendlyId));
  }

  private isTemplateKeyHidden(key: string): boolean {
    if (!key) return false;

    const hiddenIds = this.getHiddenTemplateIdSet();
    const normalizedKey = String(key).toLowerCase();
    if (hiddenIds.has(normalizedKey)) return true;

    const template = this.getAnyTemplateByKey(key);
    return this.isTemplateHidden(template);
  }

  private getAnyTemplateByKey(key: string): RecommendationTemplate {
    const k = key ? String(key).toLowerCase() : '';
    if (k && this.customTemplatesByKey && this.customTemplatesByKey[k]) {
      return this.customTemplatesByKey[k];
    }
    return getRecommendationTemplateByKey(key);
  }

  private overrideRecommendationKeyIfCustomExists(defaultKey: string): string {
    const baseKey = defaultKey ? String(defaultKey) : '';
    if (!baseKey) return baseKey;

    const defaultTemplate = getRecommendationTemplateByKey(baseKey);
    const defaultTemplateId =
      defaultTemplate && defaultTemplate.id ? String(defaultTemplate.id) : '';
    if (!defaultTemplateId) return baseKey;

    const custom = this.customTemplatesByCopiedId[defaultTemplateId];
    if (!custom) return baseKey;

    return custom.friendlyId && String(custom.friendlyId).trim()
      ? String(custom.friendlyId)
      : String(custom.id);
  }

  public generateTreatmentRecommendations(): void {
    if (this.isLocked) return;

    var category = this.getSelectedCategoryOrNull();
    if (!category) {
      this.snackBar.open('Select a dry eye category first.', 'Close', { duration: 2500 });
      return;
    }

    this.isGeneratingTemplateRecommendations = true;
    this.changeDetector.markForCheck();

    setTimeout(() => {
      try {
        var latestCategory = this.getSelectedCategoryOrNull();
        if (!latestCategory) {
          this.snackBar.open('Select a dry eye category first.', 'Close', { duration: 2500 });
          return;
        }

        var impressionsText =
          this.formGroup &&
          this.formGroup.controls &&
          this.formGroup.controls.impressions &&
          this.formGroup.controls.impressions.value
            ? String(this.formGroup.controls.impressions.value)
            : '';

        const built: TemplateRecommendation[] = [];
        const seen = new Set<string>();

        const addUnique = (rec: TemplateRecommendation) => {
          const k = rec && rec.key ? String(rec.key) : '';
          if (!k) return;
          if (this.isTemplateKeyHidden(k)) return;
          if (seen.has(k)) return;
          seen.add(k);
          built.push(rec);
        };

        // Impression-based recommendations (condition templates), honouring any
        // clinic custom template that overrides the matched condition.
        recommendTemplatesFromImpressions(impressionsText).forEach(r => {
          const detectedKey = r && r.key ? String(r.key) : '';
          if (!detectedKey) return;

          const preferredCustom = this.getPreferredCustomTemplateForRecommendationKey(detectedKey);
          if (preferredCustom) {
            const customKey = this.getTemplateRecommendationKey(preferredCustom);
            if (customKey) {
              addUnique({
                ...r,
                key: customKey,
                reason:
                  (r.reason ? r.reason + ' ' : '') +
                  `(custom template matched recommendation condition ${detectedKey})`
              });
              return;
            }
          }

          const overriddenKey = this.overrideRecommendationKeyIfCustomExists(detectedKey);
          if (overriddenKey !== detectedKey) {
            addUnique({
              ...r,
              key: overriddenKey,
              reason: (r.reason ? r.reason + ' ' : '') + '(custom template override)'
            });
            return;
          }

          // Fall back to the default template for the rule key. Use the
          // template's actual id so addUnique's hidden check resolves against
          // a real template rather than the (possibly missing) raw rule key,
          // and skip silently when no default exists in the model at all.
          const defaultMap =
            (this.treatmentTemplateService &&
              this.treatmentTemplateService.defaultTreatmentTemplateMap) ||
            ({} as any);
          const defaultTemplate = defaultMap[detectedKey];
          if (!defaultTemplate) return;

          const defaultKey = defaultTemplate.id ? String(defaultTemplate.id) : detectedKey;
          addUnique({ ...r, key: defaultKey });
        });

        // Category-based recommendation: always surface the visible treatment
        // template whose name matches the selected dry eye category, breaking
        // ties by the template with the most treatments.
        const categoryRec = this.resolveCategoryTemplateRecommendation(latestCategory);
        if (categoryRec) addUnique(categoryRec);

        this.templateRecommendations = built;
        this.templateRecommendationSections = this.buildTemplateRecommendationSections(
          this.templateRecommendations
        );
        this.recomputeTemplateRecommendationOrigins();

        this.hasGeneratedTemplateRecommendations = true;

        this.recomputeTemplateChipStates();

        if (this.templateRecommendations.length > 0) {
          this.selectTemplateRecommendation(this.templateRecommendations[0], false);
        } else {
          this.templateRecommendationSections = [];
          this.selectedTemplateRecommendation = null;
          this.selectedTemplateRecommendationReason = '';
          this.recommendedTemplateKey = '';
          this.recommendedTemplate = null;
        }
      } catch (e) {
        console.error('[generateTreatmentRecommendations] failed', e);
        this.snackBar.open('Failed to generate recommendations.', 'Close', { duration: 2500 });
      } finally {
        this.isGeneratingTemplateRecommendations = false;
        this.changeDetector.markForCheck();
      }
    }, 1000);
  }

  private buildTemplateRecommendationSections(
    recommendations: TemplateRecommendation[]
  ): TemplateRecommendationSection[] {
    const grouped: { [key in RecommendationSeveritySectionKey]: TemplateRecommendation[] } = {
      significant: [],
      probable: [],
      possible: []
    };

    (recommendations || []).forEach(rec => {
      grouped[this.getRecommendationSectionKey(rec)].push(rec);
    });

    return this.recommendationSectionOrder
      .filter(level => grouped[level].length > 0)
      .map(level => ({
        key: level,
        label: this.recommendationSectionLabelMap[level],
        recommendations: grouped[level]
      }));
  }

  private getRecommendationSectionKey(
    recommendation: TemplateRecommendation
  ): RecommendationSeveritySectionKey {
    const confidence = recommendation && recommendation.confidence;
    if (confidence === 'significant') return 'significant';
    if (confidence === 'probable') return 'probable';
    if (confidence === 'possible') return 'possible';

    if (recommendation && recommendation.source === 'impressions') return 'probable';
    return 'possible';
  }

  private getTemplateRecommendationOrigin(key: string): TemplateRecommendationOrigin {
    const normalizedKey = key ? String(key).toLowerCase() : '';
    if (!normalizedKey) return 'default';

    const customTemplate =
      this.customTemplatesByKey && this.customTemplatesByKey[normalizedKey]
        ? this.customTemplatesByKey[normalizedKey]
        : null;

    if (customTemplate) {
      return this.isSystemDefaultTemplate(customTemplate) ? 'default' : 'custom';
    }

    const defaultTemplate = getRecommendationTemplateByKey(key);
    return defaultTemplate ? 'default' : 'custom';
  }

  private recomputeTemplateRecommendationOrigins(): void {
    const originMap: { [key: string]: TemplateRecommendationOrigin } = {};
    const nameMap: { [key: string]: string } = {};

    (this.templateRecommendations || []).forEach(rec => {
      const key = rec && rec.key ? String(rec.key) : '';
      if (!key) return;
      originMap[key] = this.getTemplateRecommendationOrigin(key);
      nameMap[key] = this.getTemplateDisplayName(key);
    });

    this.templateRecommendationOriginMap = originMap;
    this.templateRecommendationNameMap = nameMap;
  }

  private getTemplateDisplayName(key: string): string {
    if (!key) return '';
    const template = this.getAnyTemplateByKey(key);
    const friendlyId = template && template.friendlyId ? String(template.friendlyId).trim() : '';
    return friendlyId || String(key);
  }

  /**
   * Resolves the dry eye category to the treatment template the clinic should
   * be shown. Matches by template name (friendlyId or id, normalized for
   * hyphen/underscore/space and trailing variant numbering) AND by the
   * recommendationConditions tag on custom templates, ignores templates hidden
   * in Clinic Setup, and — when several visible templates qualify — picks the
   * one with the most treatments.
   */
  private resolveCategoryTemplateRecommendation(
    category: DryEyeCategory
  ): TemplateRecommendation | null {
    const canonicalName = this.getCategoryTemplateName(category);
    if (!canonicalName) return null;

    const conditionKeySet = this.getCategoryDefaultKeySet(canonicalName);

    const allTemplates: any[] =
      this.treatmentTemplateService.treatmentTemplatesFormControl.value || [];
    const hiddenIds = this.getHiddenTemplateIdSet();

    const matches = allTemplates
      .map(original => ({ original, shape: this.toRecommendationTemplateShape(original) }))
      .filter(({ shape }) => {
        const id = shape.id ? String(shape.id).toLowerCase() : '';
        const friendlyId = shape.friendlyId ? String(shape.friendlyId).toLowerCase() : '';
        if (id && hiddenIds.has(id)) return false;
        if (friendlyId && hiddenIds.has(friendlyId)) return false;
        return true;
      })
      .filter(({ shape, original }) =>
        this.matchesCategory(shape, original, canonicalName, conditionKeySet)
      )
      .map(({ shape }) => shape);

    if (!matches.length) return null;

    const best = matches.reduce((bestSoFar, t) =>
      this.countTemplateTreatments(t) > this.countTemplateTreatments(bestSoFar) ? t : bestSoFar
    );

    const key = best.id ? String(best.id) : String(best.friendlyId || canonicalName);
    const display = best.friendlyId || best.id || canonicalName;
    return {
      key,
      reason: `Based on selected dry eye category (${display})`,
      source: 'category',
      confidence: 'possible'
    };
  }

  private matchesCategory(
    template: RecommendationTemplate,
    original: any,
    canonicalName: string,
    conditionKeySet: Set<string>
  ): boolean {
    const idNorm = this.normalizeTemplateName(template.id);
    const friendlyNorm = this.normalizeTemplateName(template.friendlyId);
    if (idNorm === canonicalName) return true;
    if (friendlyNorm === canonicalName) return true;

    const conditions: any[] =
      original && Array.isArray(original.recommendationConditions)
        ? original.recommendationConditions
        : [];
    for (let i = 0; i < conditions.length; i++) {
      const c = conditions[i];
      if (!c) continue;
      if (conditionKeySet.has(String(c).toLowerCase())) return true;
    }
    return false;
  }

  private normalizeTemplateName(value: any): string {
    return (value ? String(value) : '')
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\s+\d+$/, '');
  }

  private getCategoryDefaultKeySet(canonicalName: string): Set<string> {
    const keys = new Set<string>();
    const map = this.treatmentTemplateService.defaultTreatmentTemplateMap || {};
    Object.keys(map).forEach(key => {
      const template = map[key];
      if (!template) return;
      if (this.normalizeTemplateName(key) === canonicalName) {
        keys.add(key.toLowerCase());
      }
      if (
        template.friendlyId &&
        this.normalizeTemplateName(template.friendlyId) === canonicalName
      ) {
        keys.add(key.toLowerCase());
      }
    });
    return keys;
  }

  /**
   * Builds the treatment-template name for a dry eye category, e.g.
   * { severity: 'mild', type: 'aqueous' } -> "mild aqueous". This matches the
   * friendlyId of the default category treatment templates.
   */
  private getCategoryTemplateName(category: DryEyeCategory): string {
    const severityRaw =
      category && category.severity
        ? String(category.severity)
            .trim()
            .toLowerCase()
        : '';
    const typeRaw =
      category && category.type
        ? String(category.type)
            .trim()
            .toLowerCase()
        : '';

    const severity = severityRaw.startsWith('mild')
      ? 'mild'
      : severityRaw.startsWith('sev')
      ? 'severe'
      : severityRaw.startsWith('mod')
      ? 'moderate'
      : '';

    const type =
      typeRaw.startsWith('aque') || typeRaw.startsWith('aqueous')
        ? 'aqueous'
        : typeRaw.startsWith('evap') || typeRaw.startsWith('lipid')
        ? 'evaporative'
        : typeRaw.startsWith('mix')
        ? 'mixed'
        : '';

    if (!severity || !type) return '';
    return `${severity} ${type}`;
  }

  private countTemplateTreatments(template: RecommendationTemplate): number {
    if (!template) return 0;
    return (
      (template.otcEyeDrops ? template.otcEyeDrops.length : 0) +
      (template.rxEyeDrops ? template.rxEyeDrops.length : 0) +
      (template.procedures ? template.procedures.length : 0) +
      (template.exercises ? template.exercises.length : 0)
    );
  }

  public onTemplateRecommendationChipClick(rec: TemplateRecommendation): void {
    if (!rec || !rec.key) return;

    var key = String(rec.key);
    var template = this.getAnyTemplateByKey(key);

    this.selectedTemplateRecommendation = rec;
    this.selectedTemplateRecommendationReason = rec.reason || '';

    this.recommendedTemplateKey = key;
    this.recommendedTemplate = template;

    this.openAttachTemplateModal(key, template);
  }

  public attachAllRecommendedTemplates(): void {
    if (this.isLocked) return;

    const templateRecommendations = this.templateRecommendations || [];
    if (!templateRecommendations.length) return;

    const aggregated = {
      exercises: new Set<string>(),
      otcEyeDrops: new Set<string>(),
      rxEyeDrops: new Set<string>(),
      procedures: new Set<string>()
    };

    let hasAttachableTemplate = false;

    templateRecommendations.forEach(rec => {
      const key = rec && rec.key ? String(rec.key) : '';
      if (!key) return;

      const template = this.getAnyTemplateByKey(key);
      if (!template) return;

      hasAttachableTemplate = true;
      this.attachedTemplateKeys.add(key);

      this.toTreatmentIdArray(template.exercises).forEach(id => aggregated.exercises.add(id));
      this.toTreatmentIdArray(template.otcEyeDrops).forEach(id => aggregated.otcEyeDrops.add(id));
      this.toTreatmentIdArray(template.rxEyeDrops).forEach(id => aggregated.rxEyeDrops.add(id));
      this.toTreatmentIdArray(template.procedures).forEach(id => aggregated.procedures.add(id));
    });

    if (!hasAttachableTemplate) return;

    this.applyTemplateToEditablePlan(
      {
        exercises: Array.from(aggregated.exercises),
        otcEyeDrops: Array.from(aggregated.otcEyeDrops),
        rxEyeDrops: Array.from(aggregated.rxEyeDrops),
        procedures: Array.from(aggregated.procedures)
      },
      false
    );

    this.recomputeTemplateChipStates();
    this.changeDetector.markForCheck();
    this.snackBar.open('All recommended templates attached.', 'Close', { duration: 2500 });
  }

  public clearAllSelectedTreatments(): void {
    if (this.isLocked || !this.hasSelectedTreatments) return;
    if (!this.formGroup || !this.formGroup.controls) return;

    this.formGroup.controls.recommendedExercises.setValue([]);
    this.formGroup.controls.recommendedEyeDrops.setValue([]);
    this.formGroup.controls.recommendedRxEyeDrops.setValue([]);
    this.formGroup.controls.recommendedProcedures.setValue([]);

    this.attachedTemplateKeys.clear();
    this.recomputeTemplateChipStates();
    this.changeDetector.markForCheck();
    this.snackBar.open('All selected treatments cleared.', 'Close', { duration: 2500 });
  }

  private selectTemplateRecommendation(rec: TemplateRecommendation, openModal: boolean): void {
    if (!rec) return;

    var key = rec && rec.key ? String(rec.key) : '';
    if (!key) return;

    var template = this.getAnyTemplateByKey(key);

    this.selectedTemplateRecommendation = rec;
    this.selectedTemplateRecommendationReason = rec.reason || '';
    this.recommendedTemplateKey = key;
    this.recommendedTemplate = template;

    if (openModal) {
      this.openAttachTemplateModal(key, template);
    }
  }

  private getSelectedCategoryOrNull(): DryEyeCategory {
    if (!this.formGroup) return null;
    if (!this.formGroup.controls) return null;
    if (!this.formGroup.controls.category) return null;

    var category = this.formGroup.controls.category.value;
    if (!category) return null;

    return category as DryEyeCategory;
  }

  private openAttachTemplateModal(key: string, template: RecommendationTemplate): void {
    const ref = this.matDialog.open(AttachTemplateModalComponent, {
      width: '720px',
      data: { templateKey: key, template }
    });

    ref.afterClosed().subscribe(result => {
      if (!result) return;

      this.applyTemplateToEditablePlan(result);

      this.attachedTemplateKeys.add(key);

      this.recomputeTemplateChipStates();

      this.changeDetector.markForCheck();
    });
  }

  public isTemplateAttached(key: string): boolean {
    return this.attachedTemplateKeys.has(key);
  }

  private mergeTreatmentsIntoControl(control: any, incoming: Treatment[]): void {
    var existing: Treatment[] = (control && control.value ? control.value : []) as Treatment[];

    var byId: { [id: string]: Treatment } = {};

    (existing || []).forEach(t => {
      if (t && t.id) byId[t.id] = t;
    });

    (incoming || []).forEach(t => {
      if (t && t.id) byId[t.id] = t;
    });

    var merged: Treatment[] = Object.keys(byId).map(id => byId[id]);
    control.setValue(merged);
  }

  public generateAIRecommendations(): void {
    if (this.isGeneratingRecommendations) return;

    this.isGeneratingRecommendations = true;
    this.showAIRecommendations = false;

    const runRecommendations = () => {
      const parts = this.dryEyePrediction.split(' ');
      const severity = this.normalizeSeverity(parts[0] || 'moderate');
      const category = this.normalizeCategory(parts[1] || 'mixed');

      const profile: PatientProfile = {
        diagnosis: { category, severity, confidence: 0.9 },
        mimickers: [],
        riskFactors: { modifiable: [], nonModifiable: [] },
        assessmentData: this.assessmentData,
        createdAt: new Date()
      };

      this.aiPatientProfile = profile;
      this.showAIRecommendations = true;
      this.isGeneratingRecommendations = false;

      this.snackBar.open('AI recommendations generated successfully!', 'Close', { duration: 2500 });
    };

    if (this.dryEyePrediction) {
      runRecommendations();
      return;
    }

    this.waitingForPredict = true;
    this.predictDryEyeCategoryAndSeverityObservable().subscribe({
      next: (prediction: string) => {
        this.waitingForPredict = false;
        this.dryEyePrediction = prediction;
        runRecommendations();
      },
      error: err => {
        this.waitingForPredict = false;
        this.isGeneratingRecommendations = false;
        console.error('Failed to get dry eye prediction', err);
      }
    });
  }

  predictDryEyeCategoryAndSeverityObservable(): Observable<string> {
    this.categoryAndSeverityMatSpinnerOptions.active = true;
    return this.dryEyePredictionService
      .predictDryEyeCategoryAndSeverity()
      .pipe(finalize(() => (this.categoryAndSeverityMatSpinnerOptions.active = false)));
  }

  applyAIRecommendationsToForm(aiRec: TreatmentRecommendation): void {
    if (!aiRec || !aiRec.enhancedTemplate) return;

    const treatments = aiRec.enhancedTemplate;

    const mapNamesToTreatments = (names: string[]): Treatment[] => {
      return names
        .map(name => this.treatmentsService.getTreatmentByName(name))
        .filter((t): t is Treatment => !!t);
    };

    const procedures = mapNamesToTreatments(treatments.procedures || []);
    const rxEyeDrops = mapNamesToTreatments(treatments.rxEyeDrops || []);
    const otcEyeDrops = mapNamesToTreatments(treatments.otcEyeDrops || []);
    const exercises = mapNamesToTreatments(treatments.exercises || []);

    this.formGroup.controls.recommendedProcedures.setValue(procedures);
    this.formGroup.controls.recommendedRxEyeDrops.setValue(rxEyeDrops);
    this.formGroup.controls.recommendedEyeDrops.setValue(otcEyeDrops);
    this.formGroup.controls.recommendedExercises.setValue(exercises);

    this.snackBar.open('AI recommendations transferred to editable plan.', 'Close', {
      duration: 2500
    });
  }

  private promptAttachTemplateForCategory(category: DryEyeCategory): void {
    var key = this.buildTemplateKeyFromCategory(category);
    var template = this.getTemplateOrEmpty(key);

    var ref = this.matDialog.open(AttachTemplateModalComponent, {
      width: '640px',
      data: { templateKey: key, template: template }
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result) return;
      this.applyTemplateToEditablePlan(result);
    });
  }

  private buildTemplateKeyFromCategory(category: DryEyeCategory): string {
    return category.severity + '-' + category.type;
  }

  private getTemplateOrEmpty(key: string): RecommendationTemplate {
    return (
      getRecommendationTemplateByKey(key) || {
        id: key,
        friendlyId: key,
        otcEyeDrops: [],
        rxEyeDrops: [],
        procedures: [],
        exercises: []
      }
    );
  }

  private applyTemplateToEditablePlan(
    result: {
      exercises: string[];
      otcEyeDrops: string[];
      rxEyeDrops: string[];
      procedures: string[];
    },
    showSnackbar: boolean = true
  ): void {
    var toTreatments = (ids: string[]): Treatment[] => {
      return (ids || [])
        .map(id => this.treatmentsService.idToTreatmentMap.get(id))
        .filter((t): t is Treatment => !!t);
    };

    var incomingExercises = toTreatments(result.exercises);
    var incomingOtc = toTreatments(result.otcEyeDrops);
    var incomingRx = toTreatments(result.rxEyeDrops);
    var incomingProcedures = toTreatments(result.procedures);

    this.mergeTreatmentsIntoControl(
      this.formGroup.controls.recommendedExercises,
      incomingExercises
    );
    this.mergeTreatmentsIntoControl(this.formGroup.controls.recommendedEyeDrops, incomingOtc);
    this.mergeTreatmentsIntoControl(this.formGroup.controls.recommendedRxEyeDrops, incomingRx);
    this.mergeTreatmentsIntoControl(
      this.formGroup.controls.recommendedProcedures,
      incomingProcedures
    );

    if (showSnackbar) {
      this.snackBar.open('Treatment recommendations attached.', 'Close', { duration: 2500 });
    }
  }

  private toIdSet(items: any): Set<string> {
    var out = new Set<string>();
    (items || []).forEach((t: any) => {
      var id = t && t.id ? String(t.id) : '';
      if (id) out.add(id);
    });
    return out;
  }

  private unionSets(a: Set<string>, b: Set<string>): Set<string> {
    var out = new Set<string>();
    a.forEach(v => out.add(v));
    b.forEach(v => out.add(v));
    return out;
  }

  private getCurrentlyAttachedTreatmentIds(): Set<string> {
    if (!this.formGroup || !this.formGroup.controls) return new Set<string>();

    var ex = this.formGroup.controls.recommendedExercises
      ? this.toIdSet(this.formGroup.controls.recommendedExercises.value)
      : new Set<string>();

    var otc = this.formGroup.controls.recommendedEyeDrops
      ? this.toIdSet(this.formGroup.controls.recommendedEyeDrops.value)
      : new Set<string>();

    var rx = this.formGroup.controls.recommendedRxEyeDrops
      ? this.toIdSet(this.formGroup.controls.recommendedRxEyeDrops.value)
      : new Set<string>();

    var proc = this.formGroup.controls.recommendedProcedures
      ? this.toIdSet(this.formGroup.controls.recommendedProcedures.value)
      : new Set<string>();

    return this.unionSets(this.unionSets(ex, otc), this.unionSets(rx, proc));
  }

  private getTemplateTreatmentIds(key: string): string[] {
    var template = this.getAnyTemplateByKey(key);
    var ids: string[] = [];

    (template.exercises || []).forEach((id: any) => ids.push(String(id)));
    (template.otcEyeDrops || []).forEach((id: any) => ids.push(String(id)));
    (template.rxEyeDrops || []).forEach((id: any) => ids.push(String(id)));
    (template.procedures || []).forEach((id: any) => ids.push(String(id)));

    var seen: { [k: string]: boolean } = {};
    return ids.filter(x => {
      if (!x) return false;
      if (seen[x]) return false;
      seen[x] = true;
      return true;
    });
  }

  private computeChipState(key: string, attachedIds: Set<string>): TemplateChipState {
    var templateIds = this.getTemplateTreatmentIds(key);
    if (!templateIds.length) return 'recommended';

    var matched = 0;
    for (var i = 0; i < templateIds.length; i++) {
      if (attachedIds.has(templateIds[i])) matched++;
    }

    if (matched === 0) return 'recommended';
    if (matched === templateIds.length) return 'attached';
    return 'partial';
  }

  private recomputeTemplateChipStates(): void {
    var attachedIds = this.getCurrentlyAttachedTreatmentIds();

    var map: { [key: string]: TemplateChipState } = {};
    (this.templateRecommendations || []).forEach(rec => {
      var key = rec && rec.key ? String(rec.key) : '';
      if (!key) return;
      map[key] = this.computeChipState(key, attachedIds);
    });

    this.templateChipStateMap = map;
    this.updateHasSelectedTreatments();
    this.changeDetector.markForCheck();
  }

  private updateHasSelectedTreatments(): void {
    if (!this.formGroup || !this.formGroup.controls) {
      this.hasSelectedTreatments = false;
      return;
    }

    const treatmentControls = [
      this.formGroup.controls.recommendedExercises,
      this.formGroup.controls.recommendedEyeDrops,
      this.formGroup.controls.recommendedRxEyeDrops,
      this.formGroup.controls.recommendedProcedures
    ];

    this.hasSelectedTreatments = treatmentControls.some(
      control => !!control && Array.isArray(control.value) && control.value.length > 0
    );
  }

  private watchRecommendationControlsForChipState(): void {
    if (!this.formGroup || !this.formGroup.controls) return;

    var ctrls: any[] = [
      this.formGroup.controls.recommendedExercises,
      this.formGroup.controls.recommendedEyeDrops,
      this.formGroup.controls.recommendedRxEyeDrops,
      this.formGroup.controls.recommendedProcedures
    ].filter(c => !!c);

    ctrls.forEach(ctrl => {
      if (!ctrl || !ctrl.valueChanges) return;
      ctrl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.recomputeTemplateChipStates();
      });
    });
  }

  ngOnDestroy() {}
}
